import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import {
	isLocalizableHref,
	useLocalizedHref,
} from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoV3 as Logo, LogoBrandV3 as LogoBrand } from '@/libraries/wpl/foundations/src/logo-v3';

const mobileTitleCss = css({
	fontSize: '0.75rem',
	lineHeight: 'normal',
	textTransform: 'uppercase',
	display: 'none',
	margin: '0',
	padding: `${token('space.200')} 0`,
	color: wacToken('color.primary.black'),
	[media.below.md]: {
		display: 'block',
	},
});

export const MobileNavTitle = ({ title }: { title: string }) => {
	return <div css={mobileTitleCss}>{title}</div>;
};

const containerCss = css({
	paddingBlock: '24px',
	display: 'flex',
	alignItems: 'center',
	gap: token('space.150'),
});

const logoCss = css({
	display: 'flex',
	width: '44px',
	height: '44px',
	justifyContent: 'center',
	alignItems: 'center',
});

const textContainerCss = css({
	display: 'flex',
	padding: token('space.0'),
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'flex-start',
	flex: '1 0 0',
});

const nameCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h3: {
		fontSize: '0.875rem',
		fontWeight: '700',
		lineHeight: '1.2',
	},
	color: wacToken('color.primary.black'),
	fontFamily: 'Charlie Display',
	padding: token('space.0'),
	margin: '0',
	display: 'flex',
	alignItems: 'center',
});

const descriptionCss = css({
	color: wacToken('color.neutral.light800'),
	fontFamily: 'Charlie Text',
	fontSize: '0.75rem',
	fontWeight: '400',
	margin: '0',
});

const basicLinkCss = css({
	fontFamily: 'Charlie Text',
	fontSize: '16px',
	lineHeight: '20px',
	paddingBlock: '24px',
});

const pillContainerCss = css({
	color: wacToken('color.secondary.navy'),
	backgroundColor: wacToken('color.extended.blue200'),
	display: 'flex',
	alignItems: 'center',
	paddingInline: '5px',
	borderRadius: '24px',
	marginInlineStart: '6px',
});

const pillTextCss = css({
	fontSize: '0.625rem',
	fontWeight: '700',
	textTransform: 'uppercase',
});

export const Pill = () => {
	const intl = useIntl();
	return (
		<div css={pillContainerCss}>
			<span css={pillTextCss}>
				{intl.formatMessage({
					id: 'globalnav.v2.pill',
					defaultMessage: 'New',
				})}
			</span>
		</div>
	);
};

export type MobileNavCardProps = {
	key: string;
	logo?: LogoBrand;
	href: string;
	name: string;
	description?: string;
	isNew?: boolean;
	productKey?: ProductKeyTypes;
};

export const MobileNavCard = ({
	logo,
	href,
	name,
	description,
	isNew,
	productKey = 'home',
}: Omit<MobileNavCardProps, 'key'>) => {
	const localizedHref = useLocalizedHref(href);
	const formattedHref = isLocalizableHref(href) ? localizedHref : href;

	return (
		<>
			{description ? (
				<a
					href={formattedHref}
					css={containerCss}
					aria-label={name}
					target={isLocalizableHref(href) ? '' : '_blank'}
					rel={isLocalizableHref(href) ? '' : 'noreferrer'}
					data-event="clicked"
					data-event-component="linkButton"
					data-event-container="navbarDropdown"
					data-product-key={productKey}
				>
					{logo && (
						<div css={logoCss}>
							<Logo brand={logo} type="mark" appearance="brand" width={32} height={32} />
						</div>
					)}
					<div css={textContainerCss}>
						{/* Not a document heading: this is the label of the surrounding link, so it
						    must not enter the heading outline (axe heading-order). */}
						<div css={nameCss}>
							<span>{name}</span>
							{isNew && <Pill />}
						</div>
						<p css={descriptionCss}>{description}</p>
					</div>
				</a>
			) : (
				<Link
					text={name}
					href={formattedHref}
					type="primary"
					css={basicLinkCss}
					aria-label={name}
					dataEventProps={{
						'data-product-key': productKey,
						'data-event-component': 'linkButton',
						'data-event-container': 'navbarDropdown',
						'data-label': name,
						'data-event': 'clicked',
					}}
				/>
			)}
		</>
	);
};
